import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NotificationBell } from "@/components/NotificationBell";
import { PaletteToggle } from "@/components/PaletteToggle";
import { useAuth } from "@/context/AuthContext";
import { Menu, LayoutDashboard, CalendarCheck, LogOut, Compass, Users, ShieldCheck } from "lucide-react";
import { useSiteContent } from "@/context/SiteContentContext";

const LOGO_URL = "/logo_transparent.png";
const LOGO_BASE_HEIGHT = 28;
const NAV_ICONS = { workspace: Users };

function initials(name = "") {
  return name.split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();
}

export function Header() {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated, logout } = useAuth();
  const { content, navPages } = useSiteContent();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAr = i18n.language === "ar";
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const toggleLang = () => {
    i18n.changeLanguage(isAr ? "en" : "ar");
  };

  const logoSrc = content?.logo_url || LOGO_URL;
  const logoScale = content?.logo_scale || 1.3;
  const logoHeight = Math.round(LOGO_BASE_HEIGHT * logoScale);

  const navLinks = useMemo(() => {
    const rawLinks = content?.nav_links_public || [
      { id: "services", label_en: "Services", label_ar: "الخدمات", path: "/#services" },
      { id: "contact", label_en: "Contact", label_ar: "تواصل معنا", path: "/contact" },
    ];
    // Footer-only pages are intentionally excluded from the header menu.
    const cmsLinks = (navPages || [])
      .filter((p) => ["both", "header"].includes(p.nav_placement || "both"))
      .map((p) => ({
      id: p.id,
      path: p.path,
      label_en: p.label_en,
      label_ar: p.label_ar,
    }));
    return [...rawLinks, { id: "blog", path: "/blog", label_en: "Insights", label_ar: "المقالات" }, ...cmsLinks].map((l) => ({
      to: l.path,
      label: isAr ? l.label_ar : l.label_en,
      icon: NAV_ICONS[l.id] || Compass,
      authOnly: l.auth_only,
    }));
  }, [content?.nav_links_public, navPages, isAr]);

  const visibleNavLinks = useMemo(
    () => navLinks.filter((l) => !l.authOnly || isAuthenticated),
    [navLinks, isAuthenticated],
  );

  return (
    <header
      className={`sticky top-0 z-50 border-b bg-background/70 backdrop-blur supports-[backdrop-filter]:bg-background/60 transition-[height,border-color,background-color] ${
        scrolled ? "border-border/80 shadow-[0_1px_0_rgba(15,23,42,0.04)]" : "border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 sm:gap-6 px-4 sm:px-6 lg:px-8 overflow-x-clip">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo-link">
          <img
            src={logoSrc}
            alt="Start Up Gate"
            style={{ height: `${logoHeight}px` }}
            className="w-auto object-contain"
            data-testid="header-logo-image"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {visibleNavLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors hover:bg-accent hover:text-accent-foreground ${
                  location.pathname.startsWith(l.to) ? "text-primary" : "text-foreground/80"
                }`}
                data-testid={`header-nav-${l.to.replace("/", "")}`}
              >
                {l.label}
              </Link>
            ))}
        </nav>

        <div className="ms-auto flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="rounded-xl h-9 px-2.5 font-semibold text-xs tracking-wide"
            onClick={toggleLang}
            aria-label={t("header.language")}
            data-testid="header-language-toggle"
          >
            {isAr ? "ENG" : "AR"}
          </Button>

          {isAuthenticated && <NotificationBell />}

          {!isAuthenticated && (
            <Button
              onClick={() => navigate("/login")}
              className="btn-brand rounded-xl h-9 px-4 hidden sm:inline-flex"
              data-testid="header-sign-in-button"
            >
              {t("header.signIn", { defaultValue: isAr ? "تسجيل الدخول" : "Sign in" })}
            </Button>
          )}

          {isAuthenticated && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="ml-1 rounded-full" data-testid="header-user-menu">
                  <Avatar className="h-9 w-9 border border-border">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">
                      {initials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <p className="font-medium">{user?.name}</p>
                  {user?.role && (
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-normal mt-0.5">
                      {t(`roles.${user.role}`, { defaultValue: user.role })}
                    </p>
                  )}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="header-menu-dashboard">
                  <LayoutDashboard className="h-4 w-4 me-2" /> {t("header.dashboard")}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/dashboard?tab=bookings")} data-testid="header-menu-bookings">
                  <CalendarCheck className="h-4 w-4 me-2" /> {t("header.bookings")}
                </DropdownMenuItem>
                {user?.role === "admin" && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="header-menu-admin">
                    <ShieldCheck className="h-4 w-4 me-2" /> {t("header.adminPanel")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={async () => {
                    await logout();
                    navigate("/");
                  }}
                  data-testid="header-sign-out-button"
                >
                  <LogOut className="h-4 w-4 me-2" /> {t("common.signOut")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                variant="outline"
                className="md:hidden rounded-xl h-11 w-11 border-2 border-primary/40 hover:bg-primary/10 hover:border-primary/70 active:scale-95 transition-all [&_svg]:size-6"
                data-testid="header-mobile-menu-button"
                aria-label={t("header.menu", { defaultValue: "Menu" })}
              >
                <Menu strokeWidth={2.4} />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" aria-describedby={undefined} className="w-72 max-w-[85vw]">
              <SheetTitle className="sr-only">{t("header.menu", { defaultValue: "Menu" })}</SheetTitle>
              {isAuthenticated && (
                <div className="mt-6 rounded-2xl border border-border bg-muted/30 p-3 flex items-center gap-3" data-testid="header-mobile-user-card">
                  <Avatar className="h-10 w-10 border border-border">
                    <AvatarImage src={user?.picture} alt={user?.name} />
                    <AvatarFallback className="bg-primary/10 text-primary font-semibold">{initials(user?.name)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{user?.name || t("common.member", { defaultValue: "Member" })}</p>
                    <p className="text-[11px] uppercase tracking-widest text-muted-foreground truncate">{user?.role}</p>
                  </div>
                </div>
              )}
              <div className="mt-4 flex flex-col gap-2">
                {visibleNavLinks.map((l) => {
                  const active = location.pathname.startsWith(l.to.replace(/#.*/, ""));
                  return (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setMobileOpen(false)}
                      className={`flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${active ? "bg-primary/10 text-primary" : "hover:bg-accent"}`}
                    >
                      <l.icon className="h-4 w-4" /> {l.label}
                    </Link>
                  );
                })}
                {!isAuthenticated && (
                  <>
                    <div className="my-2 h-px bg-border" />
                    <Link
                      to="/login"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium btn-brand"
                      data-testid="header-mobile-sign-in"
                    >
                      <LogOut className="h-4 w-4 rotate-180" /> {isAr ? "تسجيل الدخول" : "Sign in"}
                    </Link>
                  </>
                )}
                <div className="my-2 h-px bg-border" />
                <PaletteToggle variant="row" />
                {isAuthenticated && (
                  <>
                    <div className="my-2 h-px bg-border" />
                    <Link
                      to="/dashboard"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium hover:bg-accent"
                      data-testid="header-mobile-dashboard"
                    >
                      <LayoutDashboard className="h-4 w-4" /> {t("header.dashboard")}
                    </Link>
                    {user?.role === "admin" && (
                      <Link
                        to="/admin"
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium hover:bg-accent"
                        data-testid="header-mobile-admin"
                      >
                        <ShieldCheck className="h-4 w-4" /> {t("header.adminPanel")}
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={async () => {
                        setMobileOpen(false);
                        await logout();
                        navigate("/");
                      }}
                      className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-medium hover:bg-accent text-destructive"
                      data-testid="header-mobile-signout"
                    >
                      <LogOut className="h-4 w-4" /> {t("common.signOut")}
                    </button>
                  </>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
